<template>
  <LoadingScreen v-if="isSubmitting" />
  <SuccessScreen
    v-else-if="paymentAccount"
    :payment-account="paymentAccount"
    :confirm-text="$t('ppm_modal__financials_redirect_text')"
    @confirm="$emit('go-to-financials')"
    @close="$emit('close')"
  />
  <ErrorScreen
    v-else-if="error && error.showErrorScreen"
    :message="error.description"
    @go-to-financials="$emit('go-to-financials')"
    @close="$emit('close')"
  />
  <FormLayout
    v-else
    :heading="$t('ppm_modal__dd_heading')"
    :subheading="$t('ppm_modal__form_subheading')"
    :error="error"
  >
    <ValidationObserver v-slot="{ handleSubmit }" class="dd-col">
      <form class="dd-col" @submit.prevent="handleSubmit(onSubmit)">
        <div class="dd-col">
          <PersonalDetailsStep
            v-if="currentStep === 1"
            :values="formValues"
            @on-change="handleChange"
          />
          <BillingAddressStep
            v-if="currentStep === 2"
            :values="formValues"
            @on-change="handleChange"
          />
          <BankDetailsStep
            v-if="currentStep === 3"
            :values="formValues"
            @on-change="handleChange"
          />
        </div>
        <FormFooter @on-back="handleGoBack()" />
      </form>
    </ValidationObserver>
  </FormLayout>
</template>

<script>
import get from 'lodash/get'
import pick from 'lodash/pick'
import { formatISO } from 'date-fns'
import LoadingScreen from '../LoadingScreen.vue'
import ErrorScreen from '../ErrorScreen.vue'
import FormLayout from '../../layouts/FormLayout.vue'
import FormFooter from '../../layouts/FormFooter.vue'
import BankDetailsStep from '../../forms/BankDetailsFormStep.vue'
import BillingAddressStep from '../../forms/BillingAddressFormStep.vue'
import PersonalDetailsStep from '../../forms/PersonalDetailsFormStep.vue'
import StripePaymentMethodMixin from '../../mixins/StripePaymentMethodMixin'
import PreferredPaymentMethodMixin from '../../mixins/PreferredPaymentMethodMixin'
import SuccessScreen from './DirectDepositSuccessScreen.vue'

export default {
  components: {
    LoadingScreen,
    ErrorScreen,
    SuccessScreen,
    FormLayout,
    FormFooter,
    BankDetailsStep,
    BillingAddressStep,
    PersonalDetailsStep,
  },
  mixins: [StripePaymentMethodMixin, PreferredPaymentMethodMixin],
  data() {
    return {
      currentStep: 1,
      paymentType: 'STRIPE_DIRECT_DEPOSIT',
      formValues: {
        firstName: '',
        lastName: '',
        dob: null,
        address1: '',
        address2: '',
        city: '',
        state: '',
        zip: '',
        institutionName: '',
        accountHolderName: '',
        accountNumber: '',
        confirmAccountNumber: '',
        routingNumber: '',
        confirmRoutingNumber: '',
      },
      paymentAccount: null,
      error: null,
    }
  },
  computed: {
    connectProduct() {
      return this.$store.getters['dashboard/connectProduct']
    },
    connectAccount() {
      return this.$store.getters['dashboard/connectAccount']
    },
    individualPayload() {
      const payload = pick(this.formValues, [
        'firstName',
        'lastName',
        'dob',
        'address1',
        'address2',
        'city',
        'state',
        'zip',
      ])
      payload.dob = formatISO(this.formValues.dob, { representation: 'date' })
      return payload
    },
  },
  methods: {
    async createDirectDeposit() {
      let entityPaymentVendorProductUuid = null
      const stripeConnectProductValue = get(this.connectProduct, [
        'entity_payment_vendor_product',
        'value',
      ])
      if (
        stripeConnectProductValue &&
        stripeConnectProductValue.includes('acct_')
      ) {
        entityPaymentVendorProductUuid = this.getProductUuid(
          this.connectProduct
        )
      } else {
        const response = await this.createPaymentProduct({
          ...this.formValues,
          dob: formatISO(this.formValues.dob, { representation: 'date' }),
          option: this.paymentType,
        })
        if (response.error) {
          this.error = response.error
          return
        }

        entityPaymentVendorProductUuid = this.getProductUuid(
          response.data.product
        )
      }

      if (this.connectAccount || !entityPaymentVendorProductUuid) {
        return
      }

      const response = await this.createPaymentProductAccount({
        entityPaymentVendorProductUuid,
        accountHolderName: this.formValues.accountHolderName,
        accountNumber: this.formValues.accountNumber,
        routingNumber: this.formValues.routingNumber,
        individual: this.individualPayload,
      })
      if (response.error) {
        this.error = response.error
        return
      }
      await this.persistPPM(
        response.data.account.entity_payment.entity_payment_uuid
      )
      this.paymentAccount = response.data.account
    },
    onSubmit() {
      if (this.currentStep === 3) {
        this.createDirectDeposit()
        return
      }
      this.currentStep += 1
    },
    handleChange(newFormValues) {
      this.formValues = newFormValues
    },
  },
}
</script>

<style scoped>
.dd-col {
  @apply tw-flex tw-flex-col tw-flex-1;
}
</style>
